import { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import SettingsField from "./SettingsField";
import SettingsSection from "./SettingsSection";
import { BAND_NAME_MAX, fieldId, MAX_BANDS, newBandRow } from "./settingsForm";

const ADD = "add";

/**
 * Bands: named levels with an assist (D17), in the equipment block's
 * units. One list, replaced whole on save (so row N's server error lands
 * on row N). Adding focuses the new name; removing hands focus to Add.
 */
function BandsSection({ bands, units, onChange, errorFor }) {
  // Where focus goes once the list has re-rendered: a new row's name, or
  // Add after a removal (it may only now be enabled again).
  const [focus, setFocus] = useState(null);
  const addRef = useRef(null);
  const full = bands.length >= MAX_BANDS;

  useEffect(() => {
    if (!focus) return;
    if (focus === ADD) addRef.current?.focus();
    else {
      const index = bands.findIndex((band) => band.id === focus);
      if (index >= 0) document.getElementById(fieldId(`bands.${index}.name`))?.focus();
    }
    setFocus(null);
  }, [focus, bands]);

  const setRow = (index, field, value) =>
    onChange(bands.map((band, i) => (i === index ? { ...band, [field]: value } : band)));
  const add = () => {
    if (full) return;
    const row = newBandRow();
    onChange([...bands, row]);
    setFocus(row.id);
  };
  const remove = (index) => {
    onChange(bands.filter((_, i) => i !== index));
    setFocus(ADD);
  };

  return (
    <SettingsSection
      id="training-settings-bands"
      title="Bands"
      error={errorFor("bands") || undefined}
      description={
        <>
          <p>
            For band-assisted chin-ups — planned as bodyweight + added weight − the band&apos;s
            assist, with the band whose assist comes closest — and band moves like pull-aparts,
            where the assist is the band&apos;s resistance.
          </p>
          <p>A guess is fine: the plan moves you to a lighter band as you get stronger.</p>
        </>
      }
    >
      {bands.length === 0 ? (
        <p className="m-0 text-sm text-muted-foreground">
          No bands yet. Add one to plan band-assisted chin-ups and band moves.
        </p>
      ) : (
        <ul aria-label="Your bands" className="m-0 flex list-none flex-col gap-3 p-0">
          {bands.map((band, index) => {
            const n = index + 1;
            return (
              <li key={band.id} className="rounded-md border p-3">
                <div className="mb-2 flex items-center justify-between gap-2">
                  <p className="m-0 text-sm font-medium">Band {n}</p>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-11 border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive md:h-8"
                    aria-label={`Remove band ${n}`}
                    title={`Remove band ${n}`}
                    onClick={() => remove(index)}
                  >
                    <Trash2 aria-hidden="true" />
                    Remove
                  </Button>
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <SettingsField
                    id={fieldId(`bands.${index}.name`)}
                    label={
                      <>
                        Name<span className="sr-only"> of band {n}</span>
                      </>
                    }
                    value={band.name}
                    onChange={(value) => setRow(index, "name", value)}
                    inputMode="text"
                    maxLength={BAND_NAME_MAX + 10}
                    placeholder="e.g. light"
                    error={errorFor(`bands.${index}.name`) || undefined}
                  />
                  <SettingsField
                    id={fieldId(`bands.${index}.assist`)}
                    label={
                      <>
                        Assist<span className="sr-only"> for band {n}</span>
                      </>
                    }
                    value={band.assist}
                    onChange={(value) => setRow(index, "assist", value)}
                    unit={units}
                    placeholder={units === "kg" ? "e.g. 15" : "e.g. 30"}
                    error={errorFor(`bands.${index}.assist`) || undefined}
                  />
                </div>
              </li>
            );
          })}
        </ul>
      )}
      <div className="flex flex-wrap items-center gap-3">
        <Button
          ref={addRef}
          type="button"
          variant="outline"
          className="h-11 md:h-9"
          onClick={add}
          disabled={full}
        >
          <Plus aria-hidden="true" />
          Add a band
        </Button>
        <p className="m-0 text-xs text-muted-foreground">
          {full
            ? `That's ${MAX_BANDS} — the most you can keep. Remove one to add another.`
            : `Up to ${MAX_BANDS}, each with its own name. Assist is in ${units}, the equipment's units.`}
        </p>
      </div>
    </SettingsSection>
  );
}

BandsSection.propTypes = {
  bands: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      assist: PropTypes.string.isRequired,
    }),
  ).isRequired,
  units: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  errorFor: PropTypes.func.isRequired,
};

export default BandsSection;
